import { isLoggedInAtom } from 'app/atoms/auth';
import { Radius } from 'app/components/radius';
import { Adjustable } from 'app/design/primitives/adjustable';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import useMapRadius from 'app/hooks/use-map-radius';
import type { SearchCenter } from 'app/hooks/use-search-center';
import { useSearchCenter } from 'app/hooks/use-search-center';
import { useNeighboursCountStatistics } from 'app/network/statistics';
import { getDistanceBetweenCoords, isWhiteLabel } from 'app/utils/helpers';
import { useAtomValue } from 'jotai/index';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColorValue, ScrollView, type TextStyle, View } from 'react-native';
import type { LatLng } from 'react-native-maps';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { useUISize } from 'app/utils/web-helpers';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconEye, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type NeighboursCountStats =
  number | { neighboursCount: number; neighbourListingsCount: number; usedRecentOnly: boolean };

interface MapAndRadiusVariant {
  map?: 'visible' | 'hidden';
  maximumLabel?: 'limit' | 'maximum';
  distanceLabel?: 'hidden' | 'search';
  units?: 'automatic' | 'miles';
  audience?:
    | { type: 'hidden' }
    | { type: 'neighbours'; text?: string; loadState?: 'ready' | 'loading' }
    | { type: 'onboarding-listings' };
}

function resolveMapAndRadiusVariant(variant: MapAndRadiusVariant) {
  const audience = variant.audience ?? { type: 'hidden' };
  return {
    audienceText: audience.type === 'neighbours' ? audience.text : undefined,
    describeStats: audience.type === 'onboarding-listings',
    finishedLoading: audience.type !== 'neighbours' || audience.loadState !== 'loading',
    forceMiles: variant.units === 'miles',
    listingsCount: audience.type === 'onboarding-listings',
    onboarding: audience.type === 'onboarding-listings',
    recentOnly: audience.type === 'onboarding-listings',
    showAudience: audience.type !== 'hidden',
    showMap: variant.map !== 'hidden',
    showMaxAsMax: variant.maximumLabel === 'maximum',
    showSearchDistanceLabel: variant.distanceLabel === 'search',
  };
}

function useSynchronizedRadiusInput(initialRadius: number) {
  const [radiusInput, setRadiusInput] = useState({ initialRadius, value: initialRadius });
  let currentRadiusInput = radiusInput;
  if (radiusInput.initialRadius !== initialRadius) {
    currentRadiusInput = { initialRadius, value: initialRadius };
    setRadiusInput(currentRadiusInput);
  }
  const setTmpRadius = (value: number) => setRadiusInput({ initialRadius, value });
  return { setTmpRadius, tmpRadius: currentRadiusInput.value };
}

function getInitialSearchCenter(markerPosition?: LatLng, initialRadiusCenter?: LatLng): SearchCenter {
  if (!markerPosition) {
    return {
      latitude: 0,
      longitude: 0,
      radiusCenterLatitude: 0,
      radiusCenterLongitude: 0,
    };
  }
  return {
    latitude: markerPosition.latitude,
    longitude: markerPosition.longitude,
    radiusCenterLatitude: initialRadiusCenter?.latitude ?? markerPosition.latitude,
    radiusCenterLongitude: initialRadiusCenter?.longitude ?? markerPosition.longitude,
  };
}

function useSynchronizedSearchCenter(markerPosition?: LatLng, initialRadiusCenter?: LatLng) {
  const searchCenterKey = [
    markerPosition?.latitude,
    markerPosition?.longitude,
    initialRadiusCenter?.latitude,
    initialRadiusCenter?.longitude,
  ].join(':');
  const initialSearchCenter = getInitialSearchCenter(markerPosition, initialRadiusCenter);
  const [searchCenterState, setSearchCenterState] = useState({ searchCenterKey, value: initialSearchCenter });
  let currentSearchCenterState = searchCenterState;
  if (searchCenterState.searchCenterKey !== searchCenterKey) {
    currentSearchCenterState = {
      searchCenterKey,
      value: markerPosition ? initialSearchCenter : searchCenterState.value,
    };
    setSearchCenterState(currentSearchCenterState);
  }
  const setSearchCenter = (value: SearchCenter) => setSearchCenterState({ searchCenterKey, value });
  return { searchCenter: currentSearchCenterState.value, setSearchCenter };
}

function useLastResolvedNeighboursCount(neighboursCount: NeighboursCountStats | undefined) {
  const [neighboursHistory, setNeighboursHistory] = useState<{
    latest: NeighboursCountStats | undefined;
    lastResolved: NeighboursCountStats | null;
  }>({
    latest: neighboursCount,
    lastResolved: neighboursCount ?? null,
  });
  let currentNeighboursHistory = neighboursHistory;
  if (neighboursHistory.latest !== neighboursCount) {
    currentNeighboursHistory = {
      latest: neighboursCount,
      lastResolved: neighboursCount ?? neighboursHistory.lastResolved,
    };
    setNeighboursHistory(currentNeighboursHistory);
  }
  return typeof neighboursCount === 'undefined' ? currentNeighboursHistory.lastResolved : neighboursCount;
}

function shouldFetchAudience(
  isLoggedIn: boolean,
  onboarding: boolean,
  finishedLoading: boolean,
  showAudience: boolean,
) {
  return (isLoggedIn || onboarding) && finishedLoading && showAudience;
}

interface RadiusAdjusterProps {
  forceMiles: boolean;
  lowerBound: number;
  onRadiusChangeCompleted: (radius: number) => void;
  onRadiusChanged: (radius: number) => void;
  showMaxAsMax: boolean;
  showSearchDistanceLabel: boolean;
  step: number;
  textColor: ColorValue;
  tmpRadius: number;
  upperBound: number;
}

function RadiusAdjuster({
  forceMiles,
  lowerBound,
  onRadiusChangeCompleted,
  onRadiusChanged,
  showMaxAsMax,
  showSearchDistanceLabel,
  step,
  textColor,
  tmpRadius,
  upperBound,
}: Readonly<RadiusAdjusterProps>) {
  const { t } = useTranslation('travel-distance');
  const { convertDistance, unit } = useDistanceUnit();
  const labelOnMax = isWhiteLabel()
    ? t('noLimitWhiteLabel')
    : showMaxAsMax
      ? t('noLimit', { max: convertDistance(upperBound, { forceMiles }), unit })
      : undefined;

  return (
    <>
      {showSearchDistanceLabel ? (
        <Text fontWeight="700" variant="bodyBase">
          {t('listings-filters:searchDistance')}
        </Text>
      ) : null}
      {tmpRadius ? (
        <Adjustable
          label={(value) => convertDistance(value, { forceMiles, withUnit: true })}
          labelOnMax={labelOnMax}
          max={upperBound}
          min={lowerBound}
          onChange={onRadiusChanged}
          onSlidingComplete={onRadiusChangeCompleted}
          step={step}
          value={tmpRadius}
          textColor={textColor}
          backgroundColor="surface.primary"
          padding="spacing.general.x2"
        />
      ) : null}
    </>
  );
}

function RadiusPreview({
  isSmallUI,
  onMapPanDragStart,
  onRadiusCenterChanged,
  searchCenter,
  showMap,
  tmpRadius,
}: Readonly<{
  isSmallUI: boolean;
  onMapPanDragStart?: () => void;
  onRadiusCenterChanged: (center: LatLng) => void;
  searchCenter: SearchCenter;
  showMap: boolean;
  tmpRadius: number;
}>) {
  if (!tmpRadius || !showMap || searchCenter.latitude === 0 || searchCenter.longitude === 0) return null;
  return (
    <Radius
      address={{ ...searchCenter, radius: tmpRadius }}
      aspectRatio={isSmallUI ? 1.5 : 16 / 9}
      onCenterChanged={onRadiusCenterChanged}
      onPanDrag={onMapPanDragStart}
      scrollEnabled
      showsIndoors={false}
      showsMyLocationButton={false}
      width="100%"
      zoomControlEnabled={false}
      zoomEnabled={false}
    />
  );
}

function SingleAudienceCount({
  count,
  label,
  textColor,
  valueStyle,
}: Readonly<{ count: number; label: string; textColor: ColorValue; valueStyle: TextStyle }>) {
  const iconColor = textColor === theme.colors['text.primary'] ? theme.colors['icon.interactive'] : textColor;
  const labelColor = textColor === theme.colors['text.primary'] ? 'text.secondary' : textColor;
  return (
    <>
      <Text style={valueStyle} fontSize={28}>
        {` ${count.toFixed(0)}`}
      </Text>
      <Row alignItems="center">
        <IconEye strokeWidth={ICON_STROKE_WIDTH} color={iconColor} />
        <Text style={{ color: labelColor }} fontSize={13} fontWeight="500" paddingLeft="spacing.general.x0,5">
          {label}
        </Text>
      </Row>
    </>
  );
}

function DetailedAudienceCount({
  count,
  recentOnly,
  textColor,
  valueStyle,
}: Readonly<{
  count: Exclude<NeighboursCountStats, number> | null;
  recentOnly: boolean;
  textColor: ColorValue;
  valueStyle: TextStyle;
}>) {
  const { t } = useTranslation('travel-distance');
  const dividerColor = textColor === theme.colors['text.primary'] ? theme.colors['surface.brand.default'] : textColor;
  return (
    <Row>
      <Column alignItems="center">
        <Text variant="bodyBase" fontSize={28} fontWeight="600" style={valueStyle}>
          {` ${count ? count.neighboursCount.toFixed(0) : '-'}`}
        </Text>
        <Text variant="bodyBase" style={{ color: textColor }}>
          {recentOnly ? t('neighboursRecentOnly') : t('neighbours')}
        </Text>
      </Column>

      <View
        style={{
          width: 3,
          backgroundColor: dividerColor,
          marginHorizontal: theme.spacing['spacing.general.x8'],
        }}
      />

      <Column alignItems="center">
        <Text variant="bodyBase" fontSize={28} fontWeight="600" style={valueStyle}>
          {` ${count ? count.neighbourListingsCount.toFixed(0) : '-'}`}
        </Text>
        <Text variant="bodyBase" style={{ color: textColor }}>
          {t('neighbourListings')}
        </Text>
      </Column>
    </Row>
  );
}

interface AudienceStatsProps {
  describeStats: boolean;
  effectiveNeighboursCount: NeighboursCountStats | null;
  isPending: boolean;
  recentOnly: boolean;
  showAudience: boolean;
  text?: string;
  textColor: ColorValue;
}

function AudienceStats({
  describeStats,
  effectiveNeighboursCount,
  isPending,
  recentOnly,
  showAudience,
  text,
  textColor,
}: Readonly<AudienceStatsProps>) {
  const { t } = useTranslation('travel-distance');
  if (!showAudience) return null;
  const countNumber = typeof effectiveNeighboursCount === 'number' ? effectiveNeighboursCount : null;
  const countObject =
    effectiveNeighboursCount && typeof effectiveNeighboursCount !== 'number' ? effectiveNeighboursCount : null;
  const showRecentOnly = Boolean(countObject?.usedRecentOnly && recentOnly);
  const valueStyle: TextStyle = {
    color: textColor,
    fontVariant: ['tabular-nums'],
    opacity: isPending ? 0.6 : 1,
  };

  return (
    <Column alignItems="center">
      {countNumber === null ? (
        <DetailedAudienceCount
          count={countObject}
          recentOnly={showRecentOnly}
          textColor={textColor}
          valueStyle={valueStyle}
        />
      ) : (
        <SingleAudienceCount
          count={countNumber}
          label={text ?? t('potentialAudience')}
          {...{ textColor, valueStyle }}
        />
      )}
      {describeStats && showRecentOnly ? (
        <Text
          fontSize={14}
          lineHeight={24}
          paddingTop="spacing.general.x5"
          textAlign="center"
          variant="bodyBase"
          style={{ color: textColor }}
        >
          {t('describeStatsRecentOnly')}
        </Text>
      ) : null}
    </Column>
  );
}

export function MapAndRadius({
  markerPosition,
  renderRadiusPreview,
  initialRadius,
  initialRadiusCenter,
  onChange,
  onMapPanDragStart,
  onMapPanDragEnd,
  textColor = theme.colors['text.primary'],
  variant,
}: Readonly<{
  markerPosition?: LatLng;
  renderRadiusPreview?: (radius: number) => ReactNode;
  initialRadius: number;
  initialRadiusCenter?: LatLng;
  onChange?: (radius: number, searchCenter: SearchCenter) => void;
  onMapPanDragStart?: () => void;
  onMapPanDragEnd?: () => void;
  textColor?: ColorValue;
  variant: MapAndRadiusVariant;
}>) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const resolvedVariant = resolveMapAndRadiusVariant(variant);

  const {
    radius,
    setRadius,
    defaults: { DEFAULT_STEP_IN_METERS, LOWER_BOUND_IN_METERS, UPPER_BOUND_IN_METERS },
  } = useMapRadius(undefined, initialRadius);
  const { setTmpRadius, tmpRadius } = useSynchronizedRadiusInput(initialRadius);
  const { searchCenter, setSearchCenter } = useSynchronizedSearchCenter(markerPosition, initialRadiusCenter);

  const {
    data: neighboursCount,
    isRefetching: isRefetchingNeighboursCount,
    refetch: refetchNeighboursCount,
  } = useNeighboursCountStatistics(
    radius.actual,
    { latitude: searchCenter.radiusCenterLatitude, longitude: searchCenter.radiusCenterLongitude },
    resolvedVariant.listingsCount,
    resolvedVariant.recentOnly,
    resolvedVariant.onboarding,
    {
      enabled: shouldFetchAudience(
        isLoggedIn,
        resolvedVariant.onboarding,
        resolvedVariant.finishedLoading,
        resolvedVariant.showAudience,
      ),
    },
  );

  const { isSmallUI } = useUISize();
  const effectiveNeighboursCount = useLastResolvedNeighboursCount(neighboursCount);
  const isAudienceCountPending = isRefetchingNeighboursCount || radius.actual !== tmpRadius;

  const onRadiusCenterChanged = (newCenter: LatLng) => {
    onMapPanDragEnd?.();
    const newSearchCenter = {
      ...searchCenter,
      radiusCenterLatitude: newCenter.latitude,
      radiusCenterLongitude: newCenter.longitude,
    };
    setSearchCenter(newSearchCenter);
    onChange?.(radius.actual, newSearchCenter);
    setTimeout(() => {
      if (!resolvedVariant.showAudience) {
        return;
      }
      if (resolvedVariant.finishedLoading) {
        void refetchNeighboursCount();
      } else {
        setTimeout(() => void refetchNeighboursCount(), 300);
      }
    }, 100);
  };

  const onRadiusChanged = (newRadius: number) => {
    if (newRadius < 1000) return;
    if (
      getDistanceBetweenCoords(
        searchCenter.latitude,
        searchCenter.longitude,
        searchCenter.radiusCenterLatitude,
        searchCenter.radiusCenterLongitude,
      ) > newRadius
    ) {
      // Don't allow the circle to go farther than the radius, to keep the marker inside the circle
      // Let move the radiusCenter to keep the marker inside the circle
      const newCenter = {
        latitude: searchCenter.radiusCenterLatitude,
        longitude: searchCenter.radiusCenterLongitude,
      };
      while (
        getDistanceBetweenCoords(
          searchCenter.latitude,
          searchCenter.longitude,
          newCenter.latitude,
          newCenter.longitude,
        ) > newRadius
      ) {
        const directionLat = newCenter.latitude > searchCenter.latitude ? -1 : 1;
        const directionLon = newCenter.longitude > searchCenter.longitude ? -1 : 1;
        newCenter.latitude = newCenter.latitude + directionLat * 0.001;
        newCenter.longitude = newCenter.longitude + directionLon * 0.001;
      }
      setSearchCenter({
        ...searchCenter,
        radiusCenterLatitude: newCenter.latitude,
        radiusCenterLongitude: newCenter.longitude,
      });
    }
    setTmpRadius(newRadius);
  };

  const onRadiusChangeCompleted = (newRadius: number) => {
    setRadius(newRadius);
    // setTimeout(() => void refetchNeighboursCount(), 100);
    onChange?.(newRadius, searchCenter);
  };

  return (
    <Column gap={isSmallUI ? 'spacing.general.x2' : 'spacing.general.x3'}>
      <RadiusAdjuster
        forceMiles={resolvedVariant.forceMiles}
        lowerBound={LOWER_BOUND_IN_METERS}
        onRadiusChangeCompleted={onRadiusChangeCompleted}
        onRadiusChanged={onRadiusChanged}
        showMaxAsMax={resolvedVariant.showMaxAsMax}
        showSearchDistanceLabel={resolvedVariant.showSearchDistanceLabel}
        step={DEFAULT_STEP_IN_METERS}
        textColor={textColor}
        tmpRadius={tmpRadius}
        upperBound={UPPER_BOUND_IN_METERS}
      />
      {renderRadiusPreview && resolvedVariant.showMap ? (
        renderRadiusPreview(tmpRadius)
      ) : (
        <RadiusPreview
          isSmallUI={isSmallUI}
          onMapPanDragStart={onMapPanDragStart}
          onRadiusCenterChanged={onRadiusCenterChanged}
          searchCenter={searchCenter}
          showMap={resolvedVariant.showMap}
          tmpRadius={tmpRadius}
        />
      )}
      <AudienceStats
        describeStats={resolvedVariant.describeStats}
        effectiveNeighboursCount={effectiveNeighboursCount}
        isPending={isAudienceCountPending}
        recentOnly={resolvedVariant.recentOnly}
        showAudience={resolvedVariant.showAudience}
        text={resolvedVariant.audienceText}
        textColor={textColor}
      />
    </Column>
  );
}

export interface MapSheetProps {
  onSave: (searchDistance?: number, searchCenter?: LatLng) => void;
  onReset?: () => void;
  isSaving?: boolean;
  onClose: () => void;
  markerPosition?: LatLng;
  renderRadiusPreview?: (radius: number) => ReactNode;
  initialRadius?: number;
  initialRadiusCenter?: LatLng;
  children?: ReactNode;
  title?: string;
  audienceText?: string;
  preMapHeading?: ReactNode;
  onMapPanDragStart?: () => void;
  onMapPanDragEnd?: () => void;
  variant?: MapSheetVariant;
}

interface MapSheetVariant {
  interaction?: 'enabled' | 'disabled';
  map?: 'visible' | 'hidden';
  audience?: 'visible' | 'hidden';
  maximumLabel?: 'limit' | 'maximum';
  contentPlacement?: 'above-map' | 'below-map';
  loadState?: 'ready' | 'loading';
  closeButton?: 'visible' | 'hidden';
}

function resolveMapSheetVariant(variant?: MapSheetVariant) {
  return {
    interaction: variant?.interaction ?? 'enabled',
    map: variant?.map ?? 'visible',
    audience: variant?.audience ?? 'hidden',
    maximumLabel: variant?.maximumLabel ?? 'limit',
    contentPlacement: variant?.contentPlacement ?? 'below-map',
    loadState: variant?.loadState ?? 'ready',
    closeButton: variant?.closeButton ?? 'hidden',
  } as const;
}

function buildRadiusCenter(searchCenter: SearchCenter): LatLng {
  return {
    latitude: searchCenter.radiusCenterLatitude,
    longitude: searchCenter.radiusCenterLongitude,
  };
}

function useMapSheetState(initialRadius?: number, initialRadiusCenter?: LatLng) {
  const { searchCenter: defaultSearchCenter } = useSearchCenter();
  const stateKey = [
    initialRadius,
    initialRadiusCenter?.latitude,
    initialRadiusCenter?.longitude,
    defaultSearchCenter.latitude,
    defaultSearchCenter.longitude,
    defaultSearchCenter.radiusCenterLatitude,
    defaultSearchCenter.radiusCenterLongitude,
  ].join(':');
  const initialSearchCenter = {
    ...defaultSearchCenter,
    radiusCenterLatitude: initialRadiusCenter?.latitude ?? defaultSearchCenter.radiusCenterLatitude,
    radiusCenterLongitude: initialRadiusCenter?.longitude ?? defaultSearchCenter.radiusCenterLongitude,
  };
  const [mapSheetState, setMapSheetState] = useState({
    stateKey,
    radius: initialRadius,
    searchCenter: initialSearchCenter,
  });
  let currentMapSheetState = mapSheetState;
  if (mapSheetState.stateKey !== stateKey) {
    currentMapSheetState = { stateKey, radius: initialRadius, searchCenter: initialSearchCenter };
    setMapSheetState(currentMapSheetState);
  }

  const setRadius = (radius?: number) => setMapSheetState((current) => ({ ...current, stateKey, radius }));
  const setSearchCenter = (searchCenter: SearchCenter) =>
    setMapSheetState((current) => ({ ...current, stateKey, searchCenter }));

  return {
    radius: currentMapSheetState.radius,
    searchCenter: currentMapSheetState.searchCenter,
    setRadius,
    setSearchCenter,
  };
}

function MapSheetMainContent({
  children,
  markerPosition,
  renderRadiusPreview,
  searchCenter,
  setRadius,
  setSearchCenter,
  initialRadiusForMap,
  audienceText,
  onMapPanDragStart,
  onMapPanDragEnd,
  variant,
}: Readonly<{
  children?: ReactNode;
  markerPosition?: LatLng;
  renderRadiusPreview?: (radius: number) => ReactNode;
  searchCenter: SearchCenter;
  setRadius: (radius?: number) => void;
  setSearchCenter: (searchCenter: SearchCenter) => void;
  initialRadiusForMap?: number;
  audienceText?: string;
  onMapPanDragStart?: () => void;
  onMapPanDragEnd?: () => void;
  variant: ReturnType<typeof resolveMapSheetVariant>;
}>) {
  const childrenAboveMap = variant.contentPlacement === 'above-map';
  const disabled = variant.interaction === 'disabled';
  const showCustomRadiusPreview = variant.map === 'visible' && !!renderRadiusPreview;
  return (
    <>
      {childrenAboveMap ? children : null}

      {(!disabled || showCustomRadiusPreview) && initialRadiusForMap ? (
        <MapAndRadius
          renderRadiusPreview={renderRadiusPreview}
          initialRadius={initialRadiusForMap}
          initialRadiusCenter={buildRadiusCenter(searchCenter)}
          markerPosition={markerPosition ?? searchCenter}
          onChange={(nextRadius: number, nextSearchCenter: SearchCenter) => {
            setRadius(nextRadius);
            setSearchCenter(nextSearchCenter);
          }}
          onMapPanDragEnd={onMapPanDragEnd}
          onMapPanDragStart={onMapPanDragStart}
          variant={{
            map: variant.map,
            maximumLabel: variant.maximumLabel,
            audience:
              variant.audience === 'visible'
                ? { type: 'neighbours', text: audienceText, loadState: variant.loadState }
                : { type: 'hidden' },
          }}
        />
      ) : null}

      {!childrenAboveMap ? children : null}
    </>
  );
}

function MapSheetContent({
  onSave,
  onReset,
  isSaving,
  onClose,
  markerPosition,
  renderRadiusPreview,
  initialRadius,
  initialRadiusCenter,
  children,
  title,
  audienceText,
  onMapPanDragStart,
  onMapPanDragEnd,
  variant,
}: MapSheetProps) {
  const { t } = useTranslation('travel-distance');

  const resolvedTitle = title ?? t('title');
  const resolvedVariant = resolveMapSheetVariant(variant);
  const { radius, searchCenter, setRadius, setSearchCenter } = useMapSheetState(initialRadius, initialRadiusCenter);

  return (
    <Column gap="spacing.general.x4" flex={1}>
      {resolvedVariant.closeButton === 'visible' ? (
        <Button
          onPress={onClose}
          pressableType={PressableType.GestureHandler}
          style={{
            position: 'absolute',
            right: theme.spacing['spacing.general.x2'],
            top: -theme.spacing['spacing.general.x2'],
            zIndex: 1,
          }}
          variant="plain"
        >
          <IconX strokeWidth={ICON_STROKE_WIDTH} />
        </Button>
      ) : null}

      <Text
        fontWeight="600"
        textAlign="center"
        alignSelf="center"
        variant="bodyLarge"
        marginBottom="spacing.general.x2"
      >
        {resolvedTitle}
      </Text>

      <MapSheetMainContent
        renderRadiusPreview={renderRadiusPreview}
        audienceText={audienceText}
        initialRadiusForMap={radius}
        markerPosition={markerPosition}
        onMapPanDragEnd={onMapPanDragEnd}
        onMapPanDragStart={onMapPanDragStart}
        searchCenter={searchCenter}
        setRadius={setRadius}
        setSearchCenter={setSearchCenter}
        variant={resolvedVariant}
      >
        {children}
      </MapSheetMainContent>

      <Row justifyContent="center" marginTop="spacing.general.x4" gap="spacing.general.x2">
        <Button
          flex={1}
          loading={isSaving}
          onPress={() => {
            setRadius(initialRadius);
            onReset?.();
          }}
          pressableType={PressableType.GestureHandler}
          variant="plain"
        >
          <Text color="text.primary">{t('reset')}</Text>
        </Button>

        <Button
          flex={1}
          loading={isSaving}
          onPress={() => onSave(radius, buildRadiusCenter(searchCenter))}
          pressableType={PressableType.GestureHandler}
          variant="primary"
        >
          {t('save')}
        </Button>
      </Row>
    </Column>
  );
}

interface MapSheetWithBottomSheetProps extends Omit<MapSheetProps, 'onMapPanDragStart' | 'onMapPanDragEnd'> {
  open: boolean;
}

function MapSheetWithBottomSheet({
  onSave,
  onReset,
  isSaving,
  onClose,
  open,
  markerPosition,
  renderRadiusPreview,
  initialRadius,
  initialRadiusCenter,
  children,
  title,
  audienceText,
  variant,
}: MapSheetWithBottomSheetProps) {
  const { t } = useTranslation('travel-distance');
  const { isSmallUI } = useUISize();
  const safeAreaInsets = useSafeAreaInsets();
  const sheetPadKey = isSmallUI ? 'spacing.general.x2' : 'spacing.general.x4';
  const sheetPadPx = theme.spacing[sheetPadKey];
  const sheetGap = sheetPadKey;

  const resolvedTitle = title ?? t('title');
  const resolvedVariant = resolveMapSheetVariant(variant);

  // Don't trigger onClose until the sheet has been opened at least once, and
  // restart the loading delay for every new opening.
  const [sheetLifecycle, setSheetLifecycle] = useState({
    open,
    wasOpened: open,
    finishedLoading: false,
  });
  let currentSheetLifecycle = sheetLifecycle;
  if (sheetLifecycle.open !== open) {
    currentSheetLifecycle = {
      open,
      wasOpened: sheetLifecycle.wasOpened || open,
      finishedLoading: false,
    };
    setSheetLifecycle(currentSheetLifecycle);
  }
  useEffect(() => {
    if (!open) return;
    const timeout = setTimeout(() => {
      setSheetLifecycle((current) => (current.open ? { ...current, finishedLoading: true } : current));
    }, 500);
    return () => clearTimeout(timeout);
  }, [open]);
  const { finishedLoading, wasOpened } = currentSheetLifecycle;
  const { radius, searchCenter, setRadius, setSearchCenter } = useMapSheetState(initialRadius, initialRadiusCenter);

  const [accessoryHeight, setAccessoryHeight] = useState(72);

  return (
    <BottomSheet
      gestureEnabled={false}
      onClose={() => wasOpened && onClose()}
      open={open}
      showIndicator={false}
      useBottomSafeAreaPadding={false}
      CustomHeaderComponent={
        <Row
          alignItems="center"
          justifyContent="center"
          style={{
            minHeight: 48,
            position: 'relative',
            borderTopLeftRadius: theme.borderRadii['radius.sm'],
            borderTopRightRadius: theme.borderRadii['radius.sm'],
            backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
            boxShadow: `0px 1px 1px ${theme.colors['surface.inverse.tertiary-neutral']}B3`,
          }}
        >
          <Button
            onPress={() => wasOpened && onClose()}
            pressableType={PressableType.GestureHandler}
            style={{ left: sheetPadPx, position: 'absolute', zIndex: 1 }}
            variant="plain"
            hitSlop={10}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          </Button>

          <Text
            fontWeight="600"
            paddingHorizontal="spacing.general.x12"
            textAlign="center"
            variant={isSmallUI ? 'bodyLarge' : 'headerXSmall'}
            color="text.primary"
          >
            {resolvedTitle}
          </Text>
        </Row>
      }
    >
      <Column>
        <ScrollView style={{ marginBottom: accessoryHeight }}>
          <Column gap={sheetGap} padding={sheetPadKey}>
            <MapSheetMainContent
              renderRadiusPreview={renderRadiusPreview}
              audienceText={audienceText}
              initialRadiusForMap={radius}
              markerPosition={markerPosition}
              searchCenter={searchCenter}
              setRadius={setRadius}
              setSearchCenter={setSearchCenter}
              variant={{ ...resolvedVariant, loadState: finishedLoading ? 'ready' : 'loading' }}
            >
              {children}
            </MapSheetMainContent>
          </Column>
        </ScrollView>

        <Column
          backgroundColor="surface.default"
          bottom={0}
          left={0}
          onLayout={(event) => setAccessoryHeight(Math.round(event.nativeEvent.layout.height))}
          paddingHorizontal={sheetPadKey}
          paddingTop={sheetPadKey}
          position="absolute"
          right={0}
          style={{ paddingBottom: sheetPadPx + safeAreaInsets.bottom }}
        >
          <Row gap="spacing.general.x2">
            <Button
              flex={1}
              loading={isSaving}
              onPress={() => {
                setRadius(initialRadius);
                onReset?.();
              }}
              pressableType={PressableType.GestureHandler}
              variant="plain"
            >
              <Text color="text.primary">{t('reset')}</Text>
            </Button>

            <Button
              flex={1}
              loading={isSaving}
              onPress={() => onSave(radius, buildRadiusCenter(searchCenter))}
              pressableType={PressableType.GestureHandler}
              variant="primary"
            >
              {t('save')}
            </Button>
          </Row>
        </Column>
      </Column>
    </BottomSheet>
  );
}

export default function MapSheet({
  layout = 'bottom-sheet',
  open,
  ...props
}: Readonly<MapSheetProps & { open: boolean; layout?: 'bottom-sheet' | 'inline' }>) {
  if (layout === 'bottom-sheet') {
    return <MapSheetWithBottomSheet open={open} {...props} />;
  }

  return <MapSheetContent {...props} />;
}
